import { isNumber, uniq } from "lodash";
import { useEffect, useMemo, useState, type FC } from "react";

import { AutocompleteTypeEnum, useFetchEntitiesAutocomplete } from "@ds/hooks/use-fetch-entities";
import { useRootDispatch } from "@ds/hooks";

import { experiencesService } from "@ds/modules/experiences/utils/api";
import { isIncompleteExperience } from "@ds/modules/experiences/utils/model";
import { toastShowErrorAction } from "@ds/modules/notifications/redux/actions";
import { normalizeFilters } from "@ds/modules/table-data/utils/common";

import {
  AutoComplete,
  AutoCompleteGroupItem,
  AutoCompleteGroupLabel,
  type SingleAutoCompleteProps,
} from "@ds/components/forms/autocomplete";
import { UnexpectedError } from "@ds/utils/errors";
import { UNLIMITED_PAGINATION } from "@ds/utils/query";
import { isErrorLike } from "@ds/utils/type-guards/error-guards";

import { getPlayerStatusIndicatorWithName } from "../../devices/players/components/common/status-indicator";
import { type PlayersByExperienceGroup } from "../../devices/players/utils/model";

interface CheckedExperiences {
  /** The experience ids the check was requested for */
  requestedIds: number[];
  /** The requested experiences that came back, so their status is known */
  loadedIds: number[];
  incompleteIds: number[];
}

const NO_CHECKED_EXPERIENCES: CheckedExperiences = { requestedIds: [], loadedIds: [], incompleteIds: [] };

const renderItemTemplate = (incompleteExperienceIds: number[]) => (player: Player) => {
  if (!player.experience_id || incompleteExperienceIds.includes(player.experience_id)) {
    return (
      <AutoCompleteGroupItem $isInvalid className="autocomplete-option--disabled">
        {getPlayerStatusIndicatorWithName(player)}
      </AutoCompleteGroupItem>
    );
  }

  // Deployable players are listed without a section title, so they aren't indented under one
  return <AutoCompleteGroupItem className="ml-0">{getPlayerStatusIndicatorWithName(player)}</AutoCompleteGroupItem>;
};

const GROUP_TITLES: Record<PlayersByExperienceGroup["assigned_to_status"], string | null> = {
  // Only players that can't be deployed to get a section title
  assigned: null,
  incomplete_experience: "Players in incomplete experiences",
  unassigned: "Unassigned players",
};

const renderOptionGroupTemplate = ({ assigned_to_status }: PlayersByExperienceGroup) => {
  const title = GROUP_TITLES[assigned_to_status];
  return title ? <AutoCompleteGroupLabel>{title}</AutoCompleteGroupLabel> : null;
};

export const DeploymentPlayersAutoComplete: FC<Omit<SingleAutoCompleteProps<Player>, "isLoading">> = ({
  customRef,
  inputRef,
  value,
  filterSuggestionsPredicate,
  dropdownMode,
  autoFocus,
  onBlur,
  onChange,
}) => {
  const [grouppedSuggestions, setGrouppedSuggestions] = useState<PlayersByExperienceGroup[]>([]);

  const [
    suggestions,
    onCompleteHandler,
    {
      state: { status: remoteStatus },
    },
  ] = useFetchEntitiesAutocomplete<Player>(AutocompleteTypeEnum.Players, {
    sortOrder: "asc",
    sortField: "experience_id",
    filterPredicate: filterSuggestionsPredicate,
  });

  // Players only carry their experience id, so load the suggested players' experiences in one request to see
  // which are incomplete
  const experienceIds = useMemo(
    () => uniq(suggestions.map(({ experience_id }) => experience_id)).filter(isNumber),
    [suggestions],
  );

  const [checkedExperiences, setCheckedExperiences] = useState<CheckedExperiences>(NO_CHECKED_EXPERIENCES);
  const dispatch = useRootDispatch();
  useEffect(() => {
    if (!experienceIds.length) {
      setCheckedExperiences({ ...NO_CHECKED_EXPERIENCES, requestedIds: experienceIds });
      return;
    }

    let isCancelled = false;
    experiencesService
      .getExperiences(
        {
          sortField: "id",
          sortOrder: "asc",
          // The API takes id lists as "[1,2]", which is how the tables send them too
          filters: normalizeFilters({ id: experienceIds }) as ExperienceFilters,
        },
        UNLIMITED_PAGINATION,
      )
      .then(({ items }) => {
        if (!isCancelled) {
          setCheckedExperiences({
            requestedIds: experienceIds,
            loadedIds: items.map(({ id }) => id),
            incompleteIds: items.filter(isIncompleteExperience).map(({ id }) => id),
          });
        }
      })
      .catch(err => {
        if (!isCancelled) {
          // Their status stays unknown, so all assigned players stay held back below
          setCheckedExperiences({ ...NO_CHECKED_EXPERIENCES, requestedIds: experienceIds });
          dispatch(toastShowErrorAction(isErrorLike(err) ? err : UnexpectedError, "Fetch experiences"));
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [dispatch, experienceIds]);

  const isLoadingExperiences = checkedExperiences.requestedIds !== experienceIds;

  useEffect(() => {
    // Show the list once every player's experience status is known, so a player can't be picked while it looks
    // deployable
    if (isLoadingExperiences) {
      setGrouppedSuggestions([]);
      return;
    }

    const assigned: PlayersByExperienceGroup = { assigned_to_status: "assigned", players: [] };
    const incomplete: PlayersByExperienceGroup = { assigned_to_status: "incomplete_experience", players: [] };
    const unassigned: PlayersByExperienceGroup = { assigned_to_status: "unassigned", players: [] };
    suggestions.forEach(entry => {
      if (!entry.experience_id) {
        unassigned.players.push(entry);
      } else if (checkedExperiences.incompleteIds.includes(entry.experience_id)) {
        incomplete.players.push(entry);
      } else if (checkedExperiences.loadedIds.includes(entry.experience_id)) {
        assigned.players.push(entry);
      }
      // Players whose experience didn't come back are held back, as their status is unknown
    });

    setGrouppedSuggestions([assigned, incomplete, unassigned].filter(({ players }) => players.length));
  }, [suggestions, isLoadingExperiences, checkedExperiences]);

  const itemTemplate = useMemo(
    () => renderItemTemplate(checkedExperiences.incompleteIds),
    [checkedExperiences.incompleteIds],
  );

  return (
    <AutoComplete
      field="name"
      placeholder="Enter player name"
      itemTemplate={itemTemplate}
      optionGroupLabel="assigned_to_status"
      optionGroupChildren="players"
      optionGroupTemplate={renderOptionGroupTemplate}
      customRef={customRef}
      inputRef={inputRef}
      value={value}
      suggestions={grouppedSuggestions}
      dropdownMode={dropdownMode}
      autoFocus={autoFocus}
      isLoading={remoteStatus === "loading" || isLoadingExperiences}
      emptyMessage={isLoadingExperiences ? "Loading players..." : undefined}
      onBlur={onBlur}
      onChange={onChange}
      onComplete={onCompleteHandler}
    />
  );
};
